import React, { useState } from 'react';
import { authAPI } from '../../api/auth';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { isPasswordValid, validatePassword } from '../../utils/passwordValidation';
import { PasswordInput, PasswordValidationDisplay } from '../shared';
import Button from '../ui/Button';

interface PasswordChangeFormProps {
  onSuccess?: () => void;
}

const PasswordChangeForm: React.FC<PasswordChangeFormProps> = ({ onSuccess }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState(validatePassword(''));

  const { user } = useAuth();
  const { showToast } = useToast();

  const handleNewPasswordChange = (value: string) => {
    setNewPassword(value);
    setPasswordValidation(validatePassword(value));
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentPassword.trim()) {
      showToast('Current password is required', 'error');
      return;
    }

    if (!newPassword.trim()) {
      showToast('New password is required', 'error');
      return;
    }

    if (!isPasswordValid(passwordValidation)) {
      showToast('New password does not meet the required criteria', 'error');
      return;
    }

    if (newPassword !== confirmPassword) {
      showToast('New passwords do not match', 'error');
      return;
    }

    if (currentPassword === newPassword) {
      showToast('New password must be different from current password', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await authAPI.changePassword(currentPassword, newPassword, user?.token || '');

      if (result.success) {
        showToast(result.message || 'Password changed successfully', 'success');

        // Clear form
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');

        onSuccess?.();
      } else {
        showToast(result.message || 'Failed to change password', 'error');
      }
    } catch {
      showToast('An error occurred. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <h3 className="font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
        Change Password
      </h3>

      <form onSubmit={handlePasswordChange} className="space-y-4">
        <PasswordInput
          id="currentPassword"
          label="Current Password"
          value={currentPassword}
          onChange={setCurrentPassword}
          placeholder="Enter your current password"
          autoComplete="current-password"
          disabled={isSubmitting}
          required
        />

        <PasswordInput
          id="newPassword"
          label="New Password"
          value={newPassword}
          onChange={handleNewPasswordChange}
          placeholder="Enter your new password"
          autoComplete="new-password"
          disabled={isSubmitting}
          required
        />

        <PasswordValidationDisplay password={newPassword} validation={passwordValidation} />

        <PasswordInput
          id="confirmPassword"
          label="Confirm New Password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          placeholder="Confirm your new password"
          autoComplete="new-password"
          disabled={isSubmitting}
          required
        />

        <div className="flex justify-end space-x-3 pt-4">
          <Button type="submit" variant="primary" disabled={isSubmitting} isLoading={isSubmitting}>
            Change Password
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PasswordChangeForm;
